/**
 * Data-confidence drawer row + pillar drill-down tooltips.
 */
import type { SiteComposite } from '../../types/portfolioComposite';
import type { CalculatedMetricExplanation } from './calculatedMetricExplanation';
import { explainPortfolioPillar } from './portfolioMetricExplanations';
import type { PillarId } from '../portfolio/pillarRegistry';
import {
  PILLAR_IDS,
  PILLARS,
  inapplicablePillarLabel,
  isAdvisoryPillar,
  isDecisionApplicablePillar,
} from '../portfolio/pillarRegistry';
import { formatScore2Dec } from '../portfolio/format';

export type PenaltyKey =
  | 'missingRequired'
  | 'missingRecommended'
  | 'danglingCitations'
  | 'provenanceGaps'
  | 'distributionOutliers'
  | 'reconciliationBlocks'
  | 'unresolvedHighConflicts';

const PENALTY_COPY: Record<PenaltyKey, { label: string; formula: string; improve: string }> = {
  missingRequired: {
    label: 'Missing required providers',
    formula: 'Count of required data providers absent from site reports × configured rate (capped).',
    improve: 'Run reports with full provider coverage; refetch SED sections for missing grid/regulatory data.',
  },
  missingRecommended: {
    label: 'Missing recommended providers',
    formula: 'Recommended (non-blocking) providers absent × lower rate than required.',
    improve: 'Enable optional providers in report setup for stronger evidence depth.',
  },
  danglingCitations: {
    label: 'Dangling citations',
    formula: 'Citations in narrative that do not resolve to uploaded documents × cite rate.',
    improve: 'Upload cited documents or fix [Ref N] markers in report Audit.',
  },
  provenanceGaps: {
    label: 'Provenance gaps',
    formula: 'Claims lacking user-attested or document provenance × gap rate.',
    improve: 'Confirm authored values or upload source documents in Evidence tab.',
  },
  distributionOutliers: {
    label: 'Distribution outliers',
    formula: 'Numeric claims outside expected distribution bands from QA sanity checks.',
    improve: 'Review flagged numbers in Audit and reconcile with source documents.',
  },
  reconciliationBlocks: {
    label: 'Reconciliation blocks',
    formula: 'Hard QA blocks from internal number reconciliation failures × block rate.',
    improve: 'Resolve conflicting values in Audit reconciliation pane before Recompute.',
  },
  unresolvedHighConflicts: {
    label: 'Unresolved high-severity conflicts',
    formula: 'Unresolved HIGH-severity provider-vs-document evidence conflicts × rate (capped). Withholds the verdict until reconciled.',
    improve: 'Open Evidence conflicts and pick a resolution (prefer provider or document) for each HIGH-severity row.',
  },
};

export function explainDataConfidencePenaltyRow(
  key: PenaltyKey,
  composite: SiteComposite,
): CalculatedMetricExplanation {
  const b = composite.dataConfidenceBreakdown;
  const p = b.penalties;
  const counts: Record<PenaltyKey, number> = {
    missingRequired: b.missingRequiredProviders,
    missingRecommended: b.missingRecommendedProviders,
    danglingCitations: b.danglingCitations,
    provenanceGaps: b.provenanceGaps,
    distributionOutliers: b.distributionOutliers,
    reconciliationBlocks: b.reconciliationBlocks,
    unresolvedHighConflicts: b.unresolvedHighConflicts ?? 0,
  };
  const meta = PENALTY_COPY[key];
  const count = counts[key];
  const penalty = p[key] ?? 0;
  return {
    label: meta.label,
    formula: meta.formula,
    inputs: `${count} finding(s) contributing +${penalty.toFixed(2)} to total penalty ${composite.dataCompletenessPenalty.toFixed(2)}.`,
    improve: meta.improve,
  };
}

export function explainDataConfidenceDrawerHeader(composite: SiteComposite): CalculatedMetricExplanation {
  return {
    label: 'Data confidence',
    formula: 'Aggregate QA penalty subtracted from benefit composite during portfolio Recompute.',
    inputs: `Tag ${composite.confidenceTag} · total penalty ${composite.dataCompletenessPenalty.toFixed(2)} on ${composite.siteName}.`,
    improve: 'Clear penalty rows below, then Recompute portfolio on Dashboard.',
  };
}

export function explainPillarDrillDown(
  pillarId: PillarId,
  composite: SiteComposite,
): CalculatedMetricExplanation {
  return explainPortfolioPillar(pillarId, composite);
}

export function pillarDrillDownRows(composite: SiteComposite): Array<{
  pillarId: PillarId;
  label: string;
  /**
   * The score, "Optional" for an advisory pillar, or null for a
   * model-dependent pillar that is not active yet — it carries no "Later"
   * tag (owner, 2026-09-30); its reason lives on the row's explanation hint.
   */
  score: string | null;
  missing: boolean;
  applicability: 'applicable' | 'not-applicable';
}> {
  return PILLAR_IDS.map(pillarId => {
    const p = composite.pillars[pillarId];
    const applicability = !isDecisionApplicablePillar(pillarId, p)
      ? 'not-applicable'
      : 'applicable';
    return {
      pillarId,
      label: PILLARS[pillarId].label,
      score: applicability === 'not-applicable'
        ? (isAdvisoryPillar(pillarId) ? inapplicablePillarLabel(pillarId) : null)
        : formatScore2Dec(p?.score),
      missing: p?.missing ?? false,
      applicability,
    };
  });
}
